/**
 * エラーメッセージを日本語に翻訳し、詳細な説明を提供
 */
export class ErrorMessageTranslator {
  private static errorPatterns: Array<{
    pattern: RegExp
    getMessage: (match: RegExpMatchArray) => string
    getSuggestion: (match: RegExpMatchArray) => string
    getExample?: () => string
  }> = [
    // 構文エラー
    {
      pattern: /Unexpected token '(.+)'/,
      getMessage: (match) => `予期しないトークン '${match[1]}' が見つかりました`,
      getSuggestion: () => '文法が正しいか確認してください。セミコロンや括弧の対応を確認してください。',
      getExample: () => 'for (int i = 0; i < 10; i++) { ... }'
    },
    {
      pattern: /Expected '(.+)' but got '(.+)'/,
      getMessage: (match) => `'${match[1]}' が必要ですが、'${match[2]}' が見つかりました`,
      getSuggestion: (match) => {
        if (match[1] === ';') return 'セミコロンを追加してください。'
        if (match[1] === ')') return '括弧が閉じられていません。'
        if (match[1] === '}') return '波括弧が閉じられていません。'
        return '構文を確認してください。'
      }
    },
    {
      pattern: /Missing semicolon/,
      getMessage: () => 'セミコロンが不足しています',
      getSuggestion: () => '文の終わりにセミコロン (;) を追加してください。',
      getExample: () => 'int i = 0;  // セミコロンが必要'
    },
    {
      pattern: /Unclosed (.+)/,
      getMessage: (match) => `${match[1]} が閉じられていません`,
      getSuggestion: (match) => {
        if (match[1].includes('parenthes')) return '括弧 ( ) を確認してください。'
        if (match[1].includes('brace')) return '波括弧 { } を確認してください。'
        if (match[1].includes('string')) return '文字列の引用符を確認してください。'
        return '対応する閉じ記号を追加してください。'
      }
    },
    
    // 型エラー
    {
      pattern: /Type mismatch: expected (.+), got (.+)/,
      getMessage: (match) => `型の不一致: ${match[1]}型が必要ですが、${match[2]}型が指定されました`,
      getSuggestion: () => '変数の型を確認し、適切な型に変換してください。'
    },
    {
      pattern: /Unknown type '(.+)'/,
      getMessage: (match) => `不明な型 '${match[1]}' です`,
      getSuggestion: () => 'サポートされている型: int, float, char を使用してください。',
      getExample: () => 'int count = 0;'
    },
    
    // 変数エラー
    {
      pattern: /Undefined variable '(.+)'/,
      getMessage: (match) => `変数 '${match[1]}' が定義されていません`,
      getSuggestion: () => '変数を使用する前に宣言してください。',
      getExample: () => 'int i;  // 変数の宣言'
    },
    {
      pattern: /Variable '(.+)' already declared/,
      getMessage: (match) => `変数 '${match[1]}' は既に宣言されています`,
      getSuggestion: () => '同じ名前の変数を複数回宣言することはできません。別の名前を使用してください。'
    },
    
    // ループ関連エラー
    {
      pattern: /Invalid loop condition/,
      getMessage: () => 'ループ条件が無効です',
      getSuggestion: () => 'ループ条件は真偽値を返す式である必要があります。',
      getExample: () => 'while (i < 10) { ... }'
    },
    {
      pattern: /Break statement outside loop/,
      getMessage: () => 'break文がループの外で使用されています',
      getSuggestion: () => 'break文はfor、while、do-whileループの中でのみ使用できます。'
    },
    {
      pattern: /Continue statement outside loop/,
      getMessage: () => 'continue文がループの外で使用されています',
      getSuggestion: () => 'continue文はfor、while、do-whileループの中でのみ使用できます。'
    },
    
    // 実行時エラー
    {
      pattern: /Division by zero/,
      getMessage: () => 'ゼロ除算エラー',
      getSuggestion: () => '除算の前に除数が0でないことを確認してください。',
      getExample: () => 'if (b != 0) { result = a / b; }'
    },
    {
      pattern: /Stack overflow/,
      getMessage: () => 'スタックオーバーフロー',
      getSuggestion: () => '無限ループまたは深い再帰が発生している可能性があります。ループ条件を確認してください。'
    },
    {
      pattern: /Maximum execution steps exceeded/,
      getMessage: () => '実行ステップ数の上限を超えました',
      getSuggestion: () => '無限ループの可能性があります。ループ条件が正しく終了するか確認してください。'
    }
  ]
  
  /**
   * エラーメッセージを翻訳
   */
  static translate(errorMessage: string): {
    message: string
    suggestion: string
    example?: string
  } {
    // パターンマッチング
    for (const errorPattern of this.errorPatterns) {
      const match = errorMessage.match(errorPattern.pattern)
      if (match) {
        return {
          message: errorPattern.getMessage(match),
          suggestion: errorPattern.getSuggestion(match),
          example: errorPattern.getExample?.()
        }
      }
    }
    
    // デフォルトの翻訳
    return {
      message: this.getDefaultTranslation(errorMessage),
      suggestion: '構文を確認し、エラーメッセージに従って修正してください。'
    }
  }
  
  /**
   * デフォルトの翻訳
   */
  private static getDefaultTranslation(message: string): string {
    const commonTranslations: Record<string, string> = {
      'syntax error': '構文エラー',
      'unexpected': '予期しない',
      'expected': '必要',
      'missing': '不足',
      'invalid': '無効',
      'unknown': '不明',
      'error': 'エラー'
    }
    
    let translated = message
    Object.entries(commonTranslations).forEach(([en, ja]) => {
      translated = translated.replace(new RegExp(en, 'gi'), ja)
    })
    
    return translated
  }
  
  /**
   * エラーの種類を判定
   */
  static getErrorType(errorMessage: string): 'syntax' | 'runtime' | 'logic' {
    if (errorMessage.includes('token') || errorMessage.includes('syntax') || 
        errorMessage.includes('Expected') || errorMessage.includes('semicolon')) {
      return 'syntax'
    }
    
    if (errorMessage.includes('Division by zero') || errorMessage.includes('overflow') ||
        errorMessage.includes('execution')) {
      return 'runtime'
    }
    
    return 'logic'
  }
}